import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { AdminGuard } from '@techstyle/react-admin';
import { isTruthyQueryParameter } from '@techstyle/redux-core';
import abTestModule, { setForceAllTestsControl, splitTest, clearTestsUsedOnPage } from './abTestModule';
import logger from './logger';
import useActivatedABTests from './useActivatedABTests';
var debug = logger.extend('abTestExtension');
var ABTestAdminPanel = dynamic(() => import(
/* webpackChunkName: "AdminTools" */
'./ABTestAdminPanel'));

var ABTestExtensionWrapper = _ref => {
  var children = _ref.children,
      _ref$shouldUseActivat = _ref.shouldUseActivatedTests,
      shouldUseActivatedTests = _ref$shouldUseActivat === void 0 ? false : _ref$shouldUseActivat;
  // When `shouldUseActivatedTests` is true, this will make sure that the activated tests stays up to date
  // whenever the `activatedTests` network state is invalidated.
  useActivatedABTests(shouldUseActivatedTests);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children, /*#__PURE__*/React.createElement(AdminGuard, null, /*#__PURE__*/React.createElement(ABTestAdminPanel, null)));
};

ABTestExtensionWrapper.propTypes = {
  children: PropTypes.node,
  shouldUseActivatedTests: PropTypes.bool
};
export default function abTestExtension() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      globalABTests = _ref2.globalABTests,
      shouldUseActivatedTests = _ref2.shouldUseActivatedTests;

  return registry => ({
    id: 'abTest',
    redux: {
      modules: [abTestModule({
        shouldUseActivatedTests
      })]
    },
    server: process.browser ? {} : {
      init() {
        require('./config');
      }

    },
    app: {
      getInitialProps: {
        enhance(getInitialProps) {
          return _ref3 => {
            var Component = _ref3.Component,
                ctx = _ref3.ctx;
            ctx.store.dispatch(clearTestsUsedOnPage());
            return getInitialProps({
              Component,
              ctx
            });
          };
        }

      },

      render(props, children) {
        return /*#__PURE__*/React.createElement(ABTestExtensionWrapper, {
          shouldUseActivatedTests: shouldUseActivatedTests
        }, children);
      }

    },

    page(options) {
      var abTests = options.abTests;
      var getABTests = registry.utils.getMergedArrayOption({
        globalABTests,
        abTests
      });
      return {
        getInitialProps(ctx) {
          return _asyncToGenerator(function* () {
            var _yield$ctx = yield ctx,
                pathname = _yield$ctx.pathname;

            if (ctx.isServer && !ctx.hasPreloadedState) {
              var controlParam = ctx.req.query.test_framework_force_control;
              var forceAllControl = isTruthyQueryParameter(controlParam);

              if (forceAllControl) {
                ctx.req.universalCookies.set('test_framework_force_control', controlParam);
                debug('Cookie test_framework_force_control set to: %s', controlParam);
              } else if (!ctx.req.context.anonymousServerSession) {
                // If using an anonymous server session we only want to rely on the
                // query param to enable the forced control setting, otherwise we
                // can also read the cookie.
                forceAllControl = isTruthyQueryParameter(ctx.req.universalCookies.get('test_framework_force_control'));
              }

              if (forceAllControl) {
                yield ctx.store.dispatch(setForceAllTestsControl(true));
              }
            }

            var _yield$getABTests = yield getABTests(ctx),
                _yield$getABTests2 = _slicedToArray(_yield$getABTests, 2),
                abTests = _yield$getABTests2[0],
                meta = _yield$getABTests2[1];

            if (abTests.length) {
              debug('Prefetching %s %s added by %s.', abTests.length, abTests.length === 1 ? 'A/B test' : 'A/B tests', meta.sources.join(', '));
              yield Promise.all(abTests.map(campaignCode => {
                return ctx.store.dispatch(splitTest(campaignCode, {
                  pathname
                }));
              }));
            }
          })();
        }

      };
    }

  });
}